import { IMsalContext } from '@azure/msal-react'
import { IUnitBaseConfiguration, IUnitConfiguration } from '../Models/IUnitConfiguration'
import { AuthenticationService } from './AuthenticationService'
import { getPreferredUnits } from './Global'

// Define error callback type  
type ErrorCallback = (message: string) => void;

// Define generic request body type for API calls
type RequestBody = string | number | boolean | object | FormData;

/**
 * Declares the unit system of the REQUEST PAYLOAD - not the user's display
 * preference.
 *
 * Every numeric value is converted to metric before it leaves the form (see
 * UnitAwareTextField), so the payload is always metric and the backend must
 * leave it alone. Setting this to getPreferredUnits().preferredUnits would make
 * the backend run a US->metric conversion over values that are already metric.
 */
const PAYLOAD_UNIT_SYSTEM = 'metric';

/**
 * Api service for handling api requests to the backend api 
 *
 * @export
 * @class ApiService
 */
export class ApiService {
    private context: IMsalContext
    private apiUrl: string

    constructor(ctx: IMsalContext) {
        this.context = ctx
        this.apiUrl = process.env['REACT_APP_API_URL'] as string;
    }

    /**
     * Handles error response from the api 
     *
     * @private
     * @param {Response} apiResponse
     * @param {(message: string) => void} [onError]
     * @memberof ApiService
     */
    private async handleError(apiResponse: Response, onError?: (message: string) => void): Promise<void> {
        // Clone the response before reading body to avoid "body stream already read" error
        // This allows the caller to potentially read the body again if needed
        const clonedResponse = apiResponse.clone();
        let err = await clonedResponse.text();
        let errorText = `${apiResponse.url} - (${apiResponse.status}) `;

        if (apiResponse.status >= 400 && apiResponse.status < 500) {
            try {
                const error = JSON.parse(err);
                errorText += (error.title ?? error.Title) ?? error;
                if (error.errors !== undefined) {
                    Object.keys(error.errors).sort((a, b) => (a > b ? 1 : -1)).forEach((key: string) => {
                        errorText += `\n| ${key.replace("$.", "Field with error: ")} | \n\t>> ${error.errors[key]}`;
                    });
                }
            } catch {
                errorText += err;
            }
        }
        else {
            errorText = err;
        }

        onError && onError(errorText);
        throw new Error(errorText);
    }

    
    /**
     * Get request to the api 
     *
     * @param {string} path
     * @param {(IUnitConfiguration | IUnitBaseConfiguration)} [unitInputConfiguration]
     * @param {ErrorCallback} [onError]
     * @return {Promise<Response>} 
     * @memberof ApiService
     */
    public async getAsync(
        path: string, 
        unitInputConfiguration?: IUnitConfiguration | IUnitBaseConfiguration, 
        onError?: ErrorCallback
    ): Promise<Response> {
        const authService = new AuthenticationService(this.context)

        const accessToken = await authService.getAccessToken()

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + accessToken,
                'Units': PAYLOAD_UNIT_SYSTEM,
                'UnitInputConfiguration': ''
            },
        }
        
        if (unitInputConfiguration !== undefined) {
            (requestOptions.headers as Record<string, string>)['UnitInputConfiguration'] = JSON.stringify(unitInputConfiguration);
        }

        const url: string = `${this.apiUrl}/api/${path}`
        const apiResponse = await fetch(url, requestOptions);

        if (!apiResponse.ok) {
            await this.handleError(apiResponse, onError);
        }

        return apiResponse;
    }

    
    /**
     * Post request to the api 
     *
     * @param {RequestBody} input
     * @param {string} apiPath
     * @param {(IUnitConfiguration | IUnitBaseConfiguration)} [unitInputConfiguration]
     * @param {ErrorCallback} [onError]
     * @return {Promise<Response>} 
     * @memberof ApiService
     */
    public async postAsync(
        input: RequestBody,
        apiPath: string,
        unitInputConfiguration?: IUnitConfiguration | IUnitBaseConfiguration,
        onError?: ErrorCallback
    ): Promise<Response> {
        const authService = new AuthenticationService(this.context)
        const accessToken = await authService.getAccessToken()

        // depending on what body we'll have, either treat as a file or general payload.
        if (input instanceof FormData) {
            const requestOptions: RequestInit = {
                method: 'POST',
                body: input,
                headers: {
                    ContentType: 'multipart/form-data',
                    Authorization: 'Bearer ' + accessToken,
                    'Units': PAYLOAD_UNIT_SYSTEM,
                    'UnitInputConfiguration': ''
                },
            }
            if (unitInputConfiguration !== undefined) {
                (requestOptions.headers as Record<string, string>)['UnitInputConfiguration'] = JSON.stringify(unitInputConfiguration);
            }

            const url: string = `${this.apiUrl}/api/${apiPath}`
            const apiResponse = await fetch(url, requestOptions);

            if (!apiResponse.ok) {
                await this.handleError(apiResponse, onError);
            }

            return apiResponse;

        }
        else {
            const requestOptions: RequestInit = {
                method: 'POST',
                body: JSON.stringify(input),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + accessToken,
                    'units': PAYLOAD_UNIT_SYSTEM,
                    'UnitInputConfiguration': ''
                },
            }
            if (unitInputConfiguration !== undefined) {
                (requestOptions.headers as Record<string, string>)['UnitInputConfiguration'] = JSON.stringify(unitInputConfiguration);
            }

            const url: string = `${this.apiUrl}/api/${apiPath}`
            const apiResponse = await fetch(url, requestOptions);

            if (!apiResponse.ok) {
                await this.handleError(apiResponse, onError);
            }

            return apiResponse;
        }
    }

    
    /**
     * Delete request to the api 
     *
     * @param {string} apiPath
     * @param {IUnitConfiguration} [unitInputConfiguration]
     * @param {ErrorCallback} [onError]
     * @return {Promise<Response>} 
     * @memberof ApiService
     */
    public async delete(
        apiPath: string,
        unitInputConfiguration?: IUnitConfiguration,
        onError?: ErrorCallback
    ): Promise<Response> {
        const authService = new AuthenticationService(this.context)
        const accessToken = await authService.getAccessToken()

        // depending on what body we'll have, either treat as a file or general payload.
        const requestOptions: RequestInit = {
            method: 'DELETE',
            headers: {
                ContentType: 'application/json',
                Authorization: 'Bearer ' + accessToken,
                'Units': PAYLOAD_UNIT_SYSTEM,
                'UnitInputConfiguration': ''
            },
        }
        if (unitInputConfiguration !== undefined) {
            (requestOptions.headers as Record<string, string>)['UnitInputConfiguration'] = JSON.stringify(unitInputConfiguration);
        }

        const url: string = `${this.apiUrl}/api/${apiPath}`
        const apiResponse = await fetch(url, requestOptions);

        if (!apiResponse.ok) {
            await this.handleError(apiResponse, onError);
        }

        return apiResponse;
    }

    
    /**
     * Put request to the api 
     *
     * @param {RequestBody} input
     * @param {string} apiPath
     * @param {ErrorCallback} [onError]
     * @return {Promise<Response>} 
     * @memberof ApiService
     */
    public async putAsync(
        input: RequestBody,
        apiPath: string,
        onError?: ErrorCallback
    ): Promise<Response> {
        const authService = new AuthenticationService(this.context)
        const accessToken = await authService.getAccessToken()

        // depending on what body we'll have, either treat as a file or general payload.
        const requestOptions: RequestInit = {
            method: 'PUT',
            body: JSON.stringify(input),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + accessToken,
                'units': PAYLOAD_UNIT_SYSTEM
            },
        }

        const url: string = `${this.apiUrl}/api/${apiPath}`
        const apiResponse = await fetch(url, requestOptions);

        if (!apiResponse.ok) {
            await this.handleError(apiResponse, onError);
        }

        return apiResponse;
    }

    /**
     * Submits feedback when a design parameter value is outside the allowed validation range
     * 
     * @param {object} feedback - The feedback data including form state and violation details
     * @param {ErrorCallback} [onError] - Optional error callback
     * @returns {Promise<Response>} The API response
     * @memberof ApiService
     */
    public async submitDesignRangeFeedback(
        feedback: object,
        onError?: ErrorCallback
    ): Promise<Response> {
        return await this.postAsync(feedback, 'designrangefeedback', undefined, onError);
    }
}
